import { Assert } from '@cotera/utilities';

/**
 * Metric names stored in `chat_sentiment_log.metric_name` (discriminant for row types).
 */
export const ChatSentimentMetricName = {
  SentimentLabel: 'sentiment_label',
  SentimentScore: 'sentiment_score',
  Intent: 'intent',
  Summary: 'summary',
  CompletionStatus: 'completion_status',
  CompletionSummary: 'completion_summary',
} as const;

export type ChatSentimentMetricName =
  (typeof ChatSentimentMetricName)[keyof typeof ChatSentimentMetricName];

export const SentimentLabel = {
  Positive: 'positive',
  Neutral: 'neutral',
  Frustrated: 'frustrated',
  Confused: 'confused',
  Mixed: 'mixed',
} as const;

export type SentimentLabel =
  (typeof SentimentLabel)[keyof typeof SentimentLabel];

export const CompletionStatus = {
  Success: 'success',
  Failure: 'failure',
  Friction: 'friction',
  InProgress: 'in_progress',
  Abandoned: 'abandoned',
  Unclear: 'unclear',
} as const;

export type CompletionStatus =
  (typeof CompletionStatus)[keyof typeof CompletionStatus];

/** Row shape for `chat_sentiment_log` (no ORM). */
export type ChatSentimentLogRecordData = {
  id: string;
  orgId: string;
  chatId: string;
  analysisId: string;
  observedAt: Date;
  messageCountAtObservation: number;
  lastUserMessageId: string | null;
  metricName: ChatSentimentMetricName;
  metricValue: string;
  createdAt?: Date;
  updatedAt?: Date;
};

export type SentimentLabelRecord = ChatSentimentLogRecord & {
  readonly metricName: typeof ChatSentimentMetricName.SentimentLabel;
  label(): SentimentLabel;
};

export type SentimentScoreRecord = ChatSentimentLogRecord & {
  readonly metricName: typeof ChatSentimentMetricName.SentimentScore;
  score(): number;
};

export type IntentRecord = ChatSentimentLogRecord & {
  readonly metricName: typeof ChatSentimentMetricName.Intent;
  intent(): string;
};

export type SummaryRecord = ChatSentimentLogRecord & {
  readonly metricName: typeof ChatSentimentMetricName.Summary;
  summary(): string;
};

export type CompletionStatusRecord = ChatSentimentLogRecord & {
  readonly metricName: typeof ChatSentimentMetricName.CompletionStatus;
  completionStatus(): CompletionStatus;
};

export type CompletionSummaryRecord = ChatSentimentLogRecord & {
  readonly metricName: typeof ChatSentimentMetricName.CompletionSummary;
  completionSummary(): string;
};

/** Sentiment label or score row. */
export type SentimentRecord = SentimentLabelRecord | SentimentScoreRecord;

/**
 * Hydrated row from `chat_sentiment_log`. Polymorphic on {@link ChatSentimentMetricName};
 * use `isSentimentLabelRecord()`, `isSentimentRecord()`, etc. to narrow.
 */
export abstract class ChatSentimentLogRecord {
  protected constructor(protected readonly data: ChatSentimentLogRecordData) {}

  get id(): string {
    return this.data.id;
  }

  get orgId(): string {
    return this.data.orgId;
  }

  get chatId(): string {
    return this.data.chatId;
  }

  get analysisId(): string {
    return this.data.analysisId;
  }

  get observedAt(): Date {
    return this.data.observedAt;
  }

  get messageCountAtObservation(): number {
    return this.data.messageCountAtObservation;
  }

  get lastUserMessageId(): string | null {
    return this.data.lastUserMessageId;
  }

  abstract readonly metricName: ChatSentimentMetricName;

  metricValue(): string {
    return this.data.metricValue;
  }

  toData(): ChatSentimentLogRecordData {
    return { ...this.data };
  }

  isSentimentLabelRecord(): this is SentimentLabelRecord {
    return false;
  }

  isSentimentScoreRecord(): this is SentimentScoreRecord {
    return false;
  }

  isIntentRecord(): this is IntentRecord {
    return false;
  }

  isSummaryRecord(): this is SummaryRecord {
    return false;
  }

  isCompletionStatusRecord(): this is CompletionStatusRecord {
    return false;
  }

  isCompletionSummaryRecord(): this is CompletionSummaryRecord {
    return false;
  }

  /** True for sentiment label or score rows. */
  isSentimentRecord(): this is SentimentRecord {
    return false;
  }
}

export class SentimentLabelLogRecord extends ChatSentimentLogRecord {
  readonly metricName = ChatSentimentMetricName.SentimentLabel;

  constructor(data: ChatSentimentLogRecordData) {
    super(data);
  }

  label(): SentimentLabel {
    return this.data.metricValue as SentimentLabel;
  }

  override isSentimentLabelRecord(): this is SentimentLabelRecord {
    return true;
  }

  override isSentimentRecord(): this is SentimentRecord {
    return true;
  }
}

export class SentimentScoreLogRecord extends ChatSentimentLogRecord {
  readonly metricName = ChatSentimentMetricName.SentimentScore;

  constructor(data: ChatSentimentLogRecordData) {
    super(data);
  }

  score(): number {
    const n = Number(this.data.metricValue);
    if (Number.isNaN(n)) {
      return 0;
    }
    return Math.max(-1, Math.min(1, n));
  }

  override isSentimentScoreRecord(): this is SentimentScoreRecord {
    return true;
  }

  override isSentimentRecord(): this is SentimentRecord {
    return true;
  }
}

export class IntentLogRecord extends ChatSentimentLogRecord {
  readonly metricName = ChatSentimentMetricName.Intent;

  constructor(data: ChatSentimentLogRecordData) {
    super(data);
  }

  intent(): string {
    return this.data.metricValue;
  }

  override isIntentRecord(): this is IntentRecord {
    return true;
  }
}

export class SummaryLogRecord extends ChatSentimentLogRecord {
  readonly metricName = ChatSentimentMetricName.Summary;

  constructor(data: ChatSentimentLogRecordData) {
    super(data);
  }

  summary(): string {
    return this.data.metricValue;
  }

  override isSummaryRecord(): this is SummaryRecord {
    return true;
  }
}

export class CompletionStatusLogRecord extends ChatSentimentLogRecord {
  readonly metricName = ChatSentimentMetricName.CompletionStatus;

  constructor(data: ChatSentimentLogRecordData) {
    super(data);
  }

  completionStatus(): CompletionStatus {
    return this.data.metricValue as CompletionStatus;
  }

  override isCompletionStatusRecord(): this is CompletionStatusRecord {
    return true;
  }
}

export class CompletionSummaryLogRecord extends ChatSentimentLogRecord {
  readonly metricName = ChatSentimentMetricName.CompletionSummary;

  constructor(data: ChatSentimentLogRecordData) {
    super(data);
  }

  completionSummary(): string {
    return this.data.metricValue;
  }

  override isCompletionSummaryRecord(): this is CompletionSummaryRecord {
    return true;
  }
}

export function hydrateChatSentimentLogRecord(
  data: ChatSentimentLogRecordData
): ChatSentimentLogRecord {
  switch (data.metricName) {
    case ChatSentimentMetricName.SentimentLabel:
      return new SentimentLabelLogRecord(data);
    case ChatSentimentMetricName.SentimentScore:
      return new SentimentScoreLogRecord(data);
    case ChatSentimentMetricName.Intent:
      return new IntentLogRecord(data);
    case ChatSentimentMetricName.Summary:
      return new SummaryLogRecord(data);
    case ChatSentimentMetricName.CompletionStatus:
      return new CompletionStatusLogRecord(data);
    case ChatSentimentMetricName.CompletionSummary:
      return new CompletionSummaryLogRecord(data);
    default:
      return Assert.unreachable(data.metricName);
  }
}

export function hydrateChatSentimentLogRecords(
  rows: readonly ChatSentimentLogRecordData[]
): ChatSentimentLogRecord[] {
  return rows.map((row) => hydrateChatSentimentLogRecord(row));
}
